import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ClientActivitySchema,
  ClientContactSchema,
  type ClientActivityDto,
} from '@taqon/contracts';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { getData, getPage, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { exportHref } from '../../shared/export/export-href';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { businessToday } from '../finance/line-editor';
import { ActivityDialog } from './ActivityDialog';
import {
  ACTIVITIES,
  CLIENTS,
  ClientRef,
  ConfirmDialog,
  EmployeeName,
  listLabel,
  Pager,
  ProjectRef,
  useCrmLists,
} from './common';
import { SearchFilterBar } from '../../shared/ui/filters';

const PAGE_SIZE = 50;

/** The follow-up views of the activity list (FRD-CRM-027, CRM-031). */
const VIEWS = [
  ['mine', 'My open follow-ups'],
  ['overdue', 'Overdue (open, due before today)'],
  ['open', 'All open'],
  ['all', 'All activities'],
] as const;
type View = (typeof VIEWS)[number][0];

/**
 * Client activities across clients (`/crm/activities`): the follow-up list — my open follow-ups,
 * overdue, all open, or everything — filtered and paged on the server and kept in the URL.
 */
export function ActivitiesPage() {
  const session = useSession();
  const [params, setParams] = useSearchParams();
  const requested = params.get('view');
  const view: View = VIEWS.some(([key]) => key === requested) ? (requested as View) : 'mine';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const today = businessToday(session.data?.businessTimezone);

  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: view === 'all' ? '-activity_date' : 'due_date',
  });
  if (view === 'mine') {
    query.set('filter[mine]', 'true');
    query.set('filter[status]', 'open');
  }
  if (view === 'overdue') {
    query.set('filter[status]', 'open');
    query.set('filter[due_before]', today);
  }
  if (view === 'open') query.set('filter[status]', 'open');
  const activities = useQuery({
    queryKey: ['crm', 'activities', 'list', query.toString()],
    queryFn: () => getPage(`${ACTIVITIES}?${query.toString()}`, ClientActivitySchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Client activities"
        description="Interactions with clients and their follow-ups."
        actions={
          <ExportButton recordType="crm.client_activity" href={exportHref(ACTIVITIES, query)} />
        }
      />
      <SearchFilterBar aria-label="Activity filters">
        <TextField
          select
          size="small"
          label="Show"
          value={view}
          onChange={(e) => {
            setParams({ view: e.target.value });
          }}
        >
          {VIEWS.map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </SearchFilterBar>
      {view === 'mine' && (
        <Alert severity="info" variant="outlined">
          Your follow-ups are the activities where your employee record is the responsible employee.
          A user with no linked employee record has none.
        </Alert>
      )}
      {activities.isError && <ErrorState message={activities.error.message} />}
      <Section>
        {activities.isPending && <LoadingState label="Loading activities" />}
        {activities.data?.items.length === 0 && <EmptyState title="No activities found" />}
        {activities.data !== undefined && activities.data.items.length > 0 && (
          <>
            <ActivityTable
              label="Activities"
              activities={activities.data.items}
              showClient
              today={today}
            />
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={activities.data.total}
              noun="activit(ies)"
              onPage={(next) => {
                setParams({ view, page: String(next) });
              }}
            />
          </>
        )}
      </Section>
    </Stack>
  );
}

/**
 * Activities in a table, newest first as the API returns them. Each opens its own page; the
 * client and project link where the session may; open follow-ups past their due date say so.
 * `actions` renders per-row buttons (edit, complete) where the caller allows them.
 */
export function ActivityTable({
  label,
  activities,
  showClient,
  today,
  actions,
}: {
  label: string;
  activities: readonly ClientActivityDto[];
  showClient: boolean;
  today: string;
  actions?: (activity: ClientActivityDto) => ReactNode;
}) {
  const lists = useCrmLists();
  return (
    <Table size="small" aria-label={label}>
      <TableHead>
        <TableRow>
          <TableCell>Date</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Subject</TableCell>
          {showClient && <TableCell>Client</TableCell>}
          <TableCell>Responsible</TableCell>
          <TableCell>Next action</TableCell>
          <TableCell>Due</TableCell>
          <TableCell>Status</TableCell>
          {actions !== undefined && <TableCell align="right">Actions</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {activities.map((activity) => {
          const overdue =
            activity.status === 'open' && activity.dueDate !== null && activity.dueDate < today;
          return (
            <TableRow key={activity.id} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <RecordLink to={`/crm/activities/${activity.id}`}>
                  {activity.activityDate}
                </RecordLink>
              </TableCell>
              <TableCell>
                {listLabel(lists.data?.activityType, activity.activityTypeCode)}
              </TableCell>
              <TableCell>{activity.subject ?? activity.description ?? '—'}</TableCell>
              {showClient && (
                <TableCell>
                  <ClientRef
                    id={activity.clientId}
                    label={`${activity.clientCode} — ${activity.clientName}`}
                  />
                </TableCell>
              )}
              <TableCell>
                <EmployeeName id={activity.responsibleEmployeeId} />
              </TableCell>
              <TableCell>{activity.nextAction ?? '—'}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {activity.dueDate ?? '—'}
                {overdue ? ' (overdue)' : ''}
              </TableCell>
              <TableCell>
                <StatusChip status={activity.status} />
              </TableCell>
              {actions !== undefined && (
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {actions(activity)}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** Completes an open follow-up after a confirmation (FRD-CRM-027). */
export function CompleteActivity({
  activity,
  onClose,
}: {
  activity: ClientActivityDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const complete = useMutation({
    mutationFn: () =>
      postJson(
        `${ACTIVITIES}/${activity.id}/complete`,
        {},
        ClientActivitySchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: activity.version },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crm', 'activities'] });
      await queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] });
      onClose();
    },
  });
  return (
    <ConfirmDialog
      title="Complete activity"
      message={`Mark “${activity.subject ?? activity.description ?? 'this activity'}” as completed? It leaves the open follow-ups. The change is audited.`}
      confirmLabel="Complete"
      pending={complete.isPending}
      error={complete.isError ? complete.error.message : null}
      onConfirm={() => {
        complete.mutate();
      }}
      onClose={onClose}
    />
  );
}

/** One activity (`/crm/activities/:id`), with its client, contact, project and follow-up. */
export function ActivityDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const lists = useCrmLists();
  const canChange = hasCapability(session.data, 'crm.client_activity:change');
  const [editing, setEditing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const detail = useQuery({
    queryKey: ['crm', 'activity', id],
    queryFn: () => getData(`${ACTIVITIES}/${id}`, ClientActivitySchema),
  });
  const back = (
    <Button component={RouterLink} to="/crm/activities" startIcon={<ArrowBackIcon />}>
      All activities
    </Button>
  );
  const clientId = detail.data?.clientId ?? '';
  const contactId = detail.data?.contactId ?? null;
  const contacts = useQuery({
    queryKey: ['crm', 'contacts', clientId, 'all'],
    enabled: contactId !== null && hasCapability(session.data, 'crm.client_contact:view'),
    queryFn: () => getPage(`${CLIENTS}/${clientId}/contacts?pageSize=200`, ClientContactSchema),
  });
  if (detail.isPending) return <LoadingState label="Loading the activity" />;
  if (detail.isError) {
    return <RecordFailure title="Activity" noun="activity" error={detail.error} back={back} />;
  }
  const activity = detail.data;
  const contact = contacts.data?.items.find((c) => c.id === contactId);
  return (
    <Stack spacing={3}>
      <PageHeader
        title={activity.subject ?? 'Client activity'}
        description={`${activity.activityDate} · ${activity.clientCode} — ${activity.clientName}`}
        actions={
          <>
            {back}
            {canChange && activity.status === 'open' && (
              <Button
                variant="outlined"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setCompleting(true);
                }}
              >
                Complete
              </Button>
            )}
            {canChange && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            )}
          </>
        }
      />
      <Section title="Activity">
        <Facts
          label="Activity details"
          facts={[
            ['Status', <StatusChip key="s" status={activity.status} />],
            ['Type', listLabel(lists.data?.activityType, activity.activityTypeCode)],
            ['Date', activity.activityDate],
            [
              'Client',
              <ClientRef
                key="c"
                id={activity.clientId}
                label={`${activity.clientCode} — ${activity.clientName}`}
              />,
            ],
            [
              'Contact',
              contactId === null
                ? '—'
                : (contact?.name ?? (contacts.isPending ? '…' : 'Not visible')),
            ],
            ['Responsible', <EmployeeName key="e" id={activity.responsibleEmployeeId} />],
            ['Subject', activity.subject ?? '—'],
            ['Description', activity.description ?? '—'],
            ['Next action', activity.nextAction ?? '—'],
            ['Due date', activity.dueDate ?? '—'],
            [
              'Project',
              <ProjectRef key="p" id={activity.projectId} label={activity.projectLabel} />,
            ],
          ]}
        />
      </Section>
      {/* Meeting notes and correspondence (FRD-CRM-028): attaching needs activity change. */}
      <DocumentsPanel
        recordType="crm.client_activity"
        recordId={activity.id}
        canAttach={canChange}
        title="Attachments"
      />
      <RecordHistory recordType="crm.client_activity" recordId={activity.id} />
      {editing && (
        <ActivityDialog
          key={`${activity.id}:${String(activity.version)}`}
          clientId={activity.clientId}
          activity={activity}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
      {completing && (
        <CompleteActivity
          activity={activity}
          onClose={() => {
            setCompleting(false);
          }}
        />
      )}
    </Stack>
  );
}
